import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield,
  FileText,
  ArrowRight,
  CheckCircle,
  Search,
  Clock,
  Users,
  MapPin,
  ShieldCheck,
  GraduationCap,
  Scale,
  Zap,
  Eye,
  BarChart3,
  TrendingDown,
  FileBarChart,
  Link as LinkIcon,
  ChevronRight,
  AlertCircle,
  ArrowDown,
  Star,
  MessageSquare,
  Download,
  Layers,
  CreditCard,
  Gift,
  TrendingUp,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { PublicHeader } from '../components/PublicHeader';
import { useScrollAnimation, useParallax } from '../hooks/useScrollAnimation';
import { useAuth } from '../contexts/AuthContext';
import { useForceLightMode } from '../hooks/useForceLightMode';

const AnimatedSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className = '' }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });

  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const FeatureCard: React.FC<{
  icon: React.ElementType;
  title: string;
  description: string;
  delay: number;
  iconColor?: string;
  iconBg?: string;
}> = ({ icon: Icon, title, description, delay, iconColor = 'text-primary-600', iconBg = 'from-primary-50 to-primary-100' }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <div
      ref={ref}
      className="group relative"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
        transition: `all 800ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
    >
      <div className="relative bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-medical-200/50 hover:border-primary-300/50 transition-all duration-500 hover:shadow-xl hover:shadow-primary-100/20 h-full">
        <div className={`w-12 h-12 bg-gradient-to-br ${iconBg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500`}>
          <Icon className={`h-6 w-6 ${iconColor}`} />
        </div>
        <h3 className="text-lg font-semibold text-medical-900 mb-2">
          {title}
        </h3>
        <p className="text-medical-600 text-sm leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );
};

export const LandingPage: React.FC = () => {
  useForceLightMode();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();
  const heroParallax = useParallax(0.3);
  const [activeScreen, setActiveScreen] = useState(0);
  const [filterPhase, setFilterPhase] = useState<'idle' | 'typing' | 'filtered' | 'citation'>('idle');
  const [typedChars, setTypedChars] = useState(0);
  const filterQuery = 'show early MS diagnosis events';
  const [reportPhase, setReportPhase] = useState<'options' | 'selected' | 'drafting'>('options');
  const [reportGenChars, setReportGenChars] = useState(0);

  // Report content for streaming generation effect
  const reportSections = [
    { heading: '1. Background and Pre-Existing Conditions', body: 'Jane Doe (DOB: 14/03/1968) has a documented history of multiple sclerosis, first diagnosed in 1992 following presentations with right optic neuritis and sensory myelitis. The condition was reclassified as secondary progressive by 2009, with progressive spastic paraparesis resulting in frame-dependent mobility and ill-health retirement from nursing in 2010.' },
    { heading: '2. The Index Event', body: 'On 11 November 2024, the claimant was a restrained front-seat passenger in a stationary vehicle on the A247 near Send, Surrey, when the vehicle was struck from behind. She sustained a head injury with loss of consciousness of approximately 15\u201320 minutes. She attended the Emergency Department at Royal Surrey County Hospital at 19:33.' },
    { heading: '3. Acute Management', body: 'Comprehensive trauma imaging was performed including CT head, CT cervical spine, and split bolus CT thorax/abdomen/pelvis. Clinical examination documented cervical spine tenderness at C3\u2013C7. The claimant was admitted overnight under the head injury protocol and discharged on 12 November 2024.' },
    { heading: '4. Post-Discharge Course', body: 'Following discharge, the claimant reported worsening mobility and new onset cervical pain. GP consultations documented increased spasticity in both lower limbs...' },
  ];
  const reportFullText = reportSections.map(s => s.heading + s.body).join('');
  const reportTotalChars = reportFullText.length;

  // Output demo section filter animation
  const [demoFilterPhase, setDemoFilterPhase] = useState<'full' | 'typing' | 'filtered'>('full');
  const [demoTypedChars, setDemoTypedChars] = useState(0);
  const { ref: demoRef, isVisible: demoVisible } = useScrollAnimation({ threshold: 0.3 });
  const [featuresRevealed, setFeaturesRevealed] = useState(0);

  const featureTiles = [
    { icon: Clock, label: 'Structured Chronologies', desc: 'Structured narratives from raw clinical records', colour: 'primary' },
    { icon: FileText, label: 'AI-Generated Reports', desc: 'Full reports drafted in seconds, not hours', colour: 'primary' },
    { icon: LinkIcon, label: 'Every Claim Source-Linked', desc: 'Click any statement to see the original record', colour: 'amber' },
    { icon: Layers, label: 'Any Document, Any Format', desc: 'Scanned PDFs, handwritten notes, typed letters', colour: 'primary' },
    { icon: Download, label: 'Export-Ready for Court Bundles', desc: 'Download to Word or PDF, formatted and cited', colour: 'primary' },
    { icon: MessageSquare, label: 'Case Assistant', desc: 'Ask questions about your case and get cited, contextual answers', colour: 'primary' },
  ];

  const screenCount = 5;
  const screenLabels = ['Records', 'Overview', 'Chronology', 'Reports', 'Features'];
  const screenCaptions = [
    <><span className="font-bold text-medical-900">Upload once.</span> ClinLexis reads, OCRs, and processes <span className="text-primary-600 font-semibold">every page</span> automatically.</>,
    <><span className="font-bold text-medical-900">Structured case overview</span> with key issues, index event, and treatment course — <span className="text-primary-600 font-semibold">generated in minutes</span>.</>,
    <><span className="font-bold text-medical-900">Narrative chronology</span> with <span className="text-primary-600 font-semibold">natural language filtering</span> — click any citation to see the <span className="text-primary-600 font-semibold">original source document</span>.</>,
    <><span className="font-bold text-medical-900">AI-generated reports</span> from your case data — select a report type and <span className="text-primary-600 font-semibold">ClinLexis writes it for you</span>.</>,
    <><span className="font-bold text-medical-900">Everything you need</span> to turn medical records into <span className="text-primary-600 font-semibold">litigation-ready insight</span>.</>,
  ];

  const nextScreen = useCallback(() => {
    setActiveScreen((prev) => (prev + 1) % screenCount);
  }, []);

  // Typing animation for filter on screen 2 (chronology) — starts immediately
  useEffect(() => {
    if (activeScreen === 2) {
      setFilterPhase('typing');
      setTypedChars(0);
    } else {
      setFilterPhase('idle');
      setTypedChars(0);
    }
  }, [activeScreen]);

  useEffect(() => {
    if (filterPhase === 'typing' && typedChars < filterQuery.length) {
      const timer = setTimeout(() => {
        setTypedChars((prev) => prev + 1);
      }, 60);
      return () => clearTimeout(timer);
    } else if (filterPhase === 'typing' && typedChars >= filterQuery.length) {
      // Finished typing, show filtered results after a beat
      const timer = setTimeout(() => {
        setFilterPhase('filtered');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [filterPhase, typedChars]);

  // After filtered results show for 3s, transition to citation view
  useEffect(() => {
    if (filterPhase === 'filtered') {
      const timer = setTimeout(() => {
        setFilterPhase('citation');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [filterPhase]);

  // Output demo section: trigger typing when scrolled into view
  useEffect(() => {
    if (demoVisible && demoFilterPhase === 'full') {
      const timer = setTimeout(() => {
        setDemoFilterPhase('typing');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [demoVisible, demoFilterPhase]);

  useEffect(() => {
    if (demoFilterPhase === 'typing' && demoTypedChars < filterQuery.length) {
      const timer = setTimeout(() => {
        setDemoTypedChars((prev) => prev + 1);
      }, 60);
      return () => clearTimeout(timer);
    } else if (demoFilterPhase === 'typing' && demoTypedChars >= filterQuery.length) {
      const timer = setTimeout(() => {
        setDemoFilterPhase('filtered');
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [demoFilterPhase, demoTypedChars]);

  // Report screen animation: select after 2s, start drafting after 3s
  useEffect(() => {
    if (activeScreen === 3) {
      setReportPhase('options');
      setReportGenChars(0);
      const selectTimer = setTimeout(() => setReportPhase('selected'), 2000);
      const draftTimer = setTimeout(() => setReportPhase('drafting'), 3000);
      return () => { clearTimeout(selectTimer); clearTimeout(draftTimer); };
    }
  }, [activeScreen]);

  // Fast streaming text generation effect
  useEffect(() => {
    if (reportPhase === 'drafting' && reportGenChars < reportTotalChars) {
      const timer = setTimeout(() => {
        setReportGenChars((prev) => Math.min(prev + 2, reportTotalChars));
      }, 8);
      return () => clearTimeout(timer);
    }
  }, [reportPhase, reportGenChars, reportTotalChars]);

  // Staggered feature tile reveal on screen 4
  useEffect(() => {
    if (activeScreen === 4) {
      setFeaturesRevealed(0);
      const timers: NodeJS.Timeout[] = [];
      featureTiles.forEach((_, i) => {
        timers.push(setTimeout(() => setFeaturesRevealed(i + 1), 400 + i * 350));
      });
      return () => timers.forEach(clearTimeout);
    } else {
      setFeaturesRevealed(0);
    }
  }, [activeScreen]);

  useEffect(() => {
    // Screen 2: ~2s typing + 0.5s pause + 3s filtered + 6s citation = ~12s
    // Screen 3: 3s options + 7s drafting = ~10s
    // Screen 4: 0.4s + 6×0.35s staggered tiles + hold = ~8s
    const duration = activeScreen === 2 ? 14000 : activeScreen === 3 ? 11300 : activeScreen === 4 ? 8000 : 6000;
    const timer = setTimeout(nextScreen, duration);
    return () => clearTimeout(timer);
  }, [activeScreen, nextScreen]);

  // Redirect fully-setup authenticated users to /home
  // Users with incomplete consents can still view the landing page
  useEffect(() => {
    if (!isLoading && isAuthenticated && user?.consentsCompleted !== false) {
      navigate('/home', { replace: true });
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  const handleScrollToOutput = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.getElementById('output-demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30 overflow-hidden">
      <PublicHeader fixed />

      {/* Hero Section */}
      <section className="relative pt-36 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Subtle animated background gradient */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            transform: `translateY(${heroParallax}px)`,
            background: 'radial-gradient(circle at 50% 0%, rgba(27, 147, 163, 0.1) 0%, transparent 50%)',
          }}
        />

        {/* Abstract background pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231b93a3' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px',
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text content */}
            <AnimatedSection>
              <div className="max-w-xl">
                <h1 className="text-4xl sm:text-5xl font-bold text-medical-900 leading-tight mb-6 font-display">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-primary-500">AI-Powered</span> Medical Record Analysis for Clinical Negligence and Personal Injury Claims
                </h1>
                <p className="text-lg text-medical-600 leading-relaxed mb-8">
                  Transform thousands of pages of medical records into litigation-ready chronologies,
                  issue maps, and expert-ready summaries in minutes, not weeks.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link
                    to="/demo"
                    className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all duration-300 shadow-lg shadow-primary-600/20 hover:shadow-xl hover:shadow-primary-600/30"
                  >
                    Watch the Demo
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <a
                    href="#output-demo"
                    onClick={handleScrollToOutput}
                    className="inline-flex items-center justify-center px-6 py-3 border-2 border-medical-300 text-medical-700 rounded-xl font-semibold hover:border-primary-400 hover:text-primary-700 transition-all duration-300"
                  >
                    See Sample Output
                    <ArrowDown className="ml-2 h-5 w-5" />
                  </a>
                </div>

                {/* Trust badges */}
                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs text-green-700">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    <span>NHS DSPT Compliant</span>
                  </span>
                  <span className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs text-green-700">
                    <MapPin className="h-3.5 w-3.5" />
                    <span>UK Data Residency</span>
                  </span>
                  <span className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs text-green-700">
                    <Shield className="h-3.5 w-3.5" />
                    <span>GDPR Compliant</span>
                  </span>
                </div>
              </div>
            </AnimatedSection>

            {/* Right: Product carousel */}
            <AnimatedSection delay={200}>
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-2xl border border-medical-200/50 overflow-hidden">
                  {/* Carousel content */}
                  <div className="relative" style={{ height: '420px' }}>

                    {/* Screen 0: Records — document upload */}
                    <div className="absolute inset-0 transition-all duration-500 ease-in-out overflow-hidden flex flex-col" style={{ opacity: activeScreen === 0 ? 1 : 0, transform: activeScreen === 0 ? 'translateX(0)' : 'translateX(20px)' }}>
                      {/* Top nav tabs */}
                      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-medical-100 overflow-x-auto text-[8px] text-medical-500 whitespace-nowrap">
                        {['Records', 'Overview', 'Claimant', 'Chronology', 'Expert Evidence', 'Images', 'Body Map', 'Clinical Standards', 'Evidence Integrity', 'Causation'].map((tab) => (
                          <span key={tab} className={`px-1.5 py-0.5 ${tab === 'Records' ? 'text-primary-600 font-semibold border-b-2 border-primary-500' : 'text-medical-500'}`}>{tab}</span>
                        ))}
                      </div>
                      <div className="flex-1 px-4 py-3 overflow-hidden">
                        {/* Upload area */}
                        <div className="border-2 border-dashed border-medical-200 rounded-xl p-4 text-center mb-3 bg-medical-50/30">
                          <div className="w-8 h-8 mx-auto mb-2 rounded-full bg-primary-50 flex items-center justify-center">
                            <FileText className="w-4 h-4 text-primary-500" />
                          </div>
                          <p className="text-[10px] font-semibold text-medical-800">Drag and drop medical records here</p>
                          <p className="text-[9px] text-medical-500 mt-0.5">PDF files up to 200MB &middot; or click to browse</p>
                        </div>
                        {/* Uploaded files list */}
                        <div className="space-y-1.5">
                          <p className="text-[9px] font-semibold text-medical-600 uppercase tracking-wide">Uploaded Records &mdash; 4 files, 847 pages</p>
                          {[
                            { name: 'GP_Records_2010-2024.pdf', pages: 312, status: 'Processed' },
                            { name: 'Royal_Surrey_Hospital_Notes.pdf', pages: 287, status: 'Processed' },
                            { name: 'Neurology_Outpatient_Letters.pdf', pages: 156, status: 'Processed' },
                            { name: 'Radiology_Reports_1992-2024.pdf', pages: 92, status: 'Processing' },
                          ].map((file) => (
                            <div key={file.name} className="flex items-center gap-2 px-2.5 py-1.5 bg-white rounded-lg border border-medical-100">
                              <FileText className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-[9px] font-medium text-medical-800 truncate">{file.name}</p>
                                <p className="text-[8px] text-medical-500">{file.pages} pages</p>
                              </div>
                              <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded ${file.status === 'Processed' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>{file.status}</span>
                            </div>
                          ))}
                        </div>
                        {/* Processing summary */}
                        <div className="mt-3 p-2.5 bg-primary-50/50 rounded-lg border border-primary-100">
                          <div className="flex items-center justify-between text-[9px]">
                            <span className="text-medical-700 font-medium">Total pages processed</span>
                            <span className="text-primary-700 font-bold">755 / 847</span>
                          </div>
                          <div className="mt-1.5 h-1.5 bg-primary-100 rounded-full overflow-hidden">
                            <div className="h-full bg-primary-500 rounded-full transition-all duration-1000" style={{ width: '89%' }} />
                          </div>
                          <p className="text-[8px] text-medical-500 mt-1">OCR complete &middot; Extracting clinical events &middot; ~2 min remaining</p>
                        </div>
                      </div>
                    </div>

                    {/* Screen 1: Overview — case summary */}
                    <div className="absolute inset-0 transition-all duration-500 ease-in-out overflow-hidden flex flex-col" style={{ opacity: activeScreen === 1 ? 1 : 0, transform: activeScreen === 1 ? 'translateX(0)' : 'translateX(20px)' }}>
                      {/* Top nav tabs */}
                      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-medical-100 overflow-x-auto text-[8px] text-medical-500 whitespace-nowrap">
                        {['Records', 'Overview', 'Claimant', 'Chronology', 'Expert Evidence', 'Images', 'Body Map', 'Clinical Standards', 'Evidence Integrity', 'Causation'].map((tab) => (
                          <span key={tab} className={`px-1.5 py-0.5 ${tab === 'Overview' ? 'text-primary-600 font-semibold border-b-2 border-primary-500' : 'text-medical-500'}`}>{tab}</span>
                        ))}
                      </div>
                      <div className="flex-1 px-4 py-3 overflow-hidden space-y-2.5">
                        {/* Case header */}
                        {/* Case Overview card */}
                        <div className="p-2.5 rounded-lg border border-medical-200 bg-white">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Star className="w-3 h-3 text-primary-500" />
                            <p className="text-[9px] font-bold text-medical-900">Case Overview</p>
                          </div>
                          <p className="text-[8.5px] text-medical-700 leading-relaxed">Jane Doe is a female born on 14 March 1968, aged 56 at the material time, with a background of secondary progressive multiple sclerosis diagnosed in 1992<sup className="text-amber-500 text-[7px]">*</sup> and established as secondary progressive by 2009<sup className="text-amber-500 text-[7px]">*</sup>, resulting in frame-dependent mobility and ill-health retirement from nursing by 2010<sup className="text-amber-500 text-[7px]">*</sup>. On 11 November 2024, the claimant was involved in a road traffic collision on the A247 near Send, Surrey<sup className="text-amber-500 text-[7px]">*</sup>. She sustained a head injury with loss of consciousness of approximately 15&ndash;20 minutes<sup className="text-amber-500 text-[7px]">**</sup>. She attended the Emergency Department at Royal Surrey County Hospital at 19:33 that evening, where comprehensive CT trauma imaging of the head, cervical spine, thorax, abdomen and pelvis was performed<sup className="text-amber-500 text-[7px]">*</sup>. Pre-existing frame-dependent mobility has deteriorated following the collision, with new onset cervical pain and increased lower limb spasticity documented<sup className="text-amber-500 text-[7px]">*</sup>.</p>
                        </div>
                        {/* Clinical overview sections — full-width stacked cards */}
                        <div className="space-y-2">
                          <div className="p-2.5 rounded-lg border border-red-200 bg-gradient-to-br from-red-50 to-orange-50">
                            <div className="flex items-center gap-1.5 mb-1">
                              <AlertCircle className="w-3 h-3 text-red-600" />
                              <p className="text-[9px] font-bold text-medical-900">The Index Event</p>
                            </div>
                            <p className="text-[8.5px] text-medical-700 leading-relaxed">On 11 November 2024, at approximately 18:15, the claimant was a restrained front-seat passenger in a stationary vehicle on the A247 near Send, Surrey, when the vehicle was struck from behind by another vehicle travelling at approximately 40 mph<sup className="text-amber-500 text-[7px]">**</sup>. She sustained a head injury with loss of consciousness of approximately 15&ndash;20 minutes<sup className="text-amber-500 text-[7px]">*</sup>. She was not assessed by paramedics at the scene<sup className="text-amber-500 text-[7px]">*</sup>. She attended the Emergency Department at 19:33 on 11 November 2024<sup className="text-amber-500 text-[7px]">*</sup>. Clinical examination documented tenderness at C3&ndash;C7 spinal levels with reduced range of cervical motion<sup className="text-amber-500 text-[7px]">*</sup>.</p>
                          </div>
                          <div className="p-2.5 rounded-lg border border-purple-200 bg-gradient-to-br from-purple-50 to-violet-50">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Zap className="w-3 h-3 text-purple-600" />
                              <p className="text-[9px] font-bold text-medical-900">Treatment Course</p>
                            </div>
                            <p className="text-[8.5px] text-medical-700 leading-relaxed">Upon arrival at the Emergency Department, the claimant underwent comprehensive trauma imaging including a non-contrast CT head<sup className="text-amber-500 text-[7px]">*</sup>, CT cervical spine<sup className="text-amber-500 text-[7px]">*</sup>, and a split bolus CT of the thorax, abdomen and pelvis<sup className="text-amber-500 text-[7px]">*</sup>. IV access was achieved and observation with cardiac monitoring and head injury monitoring was undertaken<sup className="text-amber-500 text-[7px]">*</sup>. The claimant was discharged on 12 November 2024 with head injury advice<sup className="text-amber-500 text-[7px]">*</sup>.</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Screen 2: Chronology + AI filter */}
                    <div className="absolute inset-0 transition-all duration-500 ease-in-out overflow-hidden flex flex-col" style={{ opacity: activeScreen === 2 ? 1 : 0, transform: activeScreen === 2 ? 'translateX(0)' : 'translateX(20px)' }}>
                      {/* Top nav tabs */}
                      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-medical-100 overflow-x-auto text-[8px] text-medical-500 whitespace-nowrap">
                        {['Records', 'Overview', 'Claimant', 'Chronology', 'Expert Evidence', 'Images', 'Body Map', 'Clinical Standards', 'Evidence Integrity', 'Causation'].map((tab) => (
                          <span key={tab} className={`px-1.5 py-0.5 ${tab === 'Chronology' ? 'text-primary-600 font-semibold border-b-2 border-primary-500' : 'text-medical-500'}`}>{tab}</span>
                        ))}
                      </div>
                      {/* Search bar with typing animation */}
                      <div className="mx-3 mt-2 mb-1.5">
                        <div className="flex items-center bg-white border border-medical-200 rounded-lg px-2.5 py-1.5">
                          <Search className="h-3 w-3 text-medical-400 mr-2 flex-shrink-0" />
                          {filterPhase === 'idle' ? (
                            <span className="text-medical-400 text-[10px]">104 of 108 events &mdash; ask anything, e.g. &ldquo;show only surgical events&rdquo;</span>
                          ) : (
                            <>
                              <span className="text-medical-800 text-[10px]">{filterQuery.slice(0, typedChars)}</span>
                              {filterPhase === 'typing' && <span className="w-0.5 h-3 bg-primary-500 animate-pulse ml-0.5" />}
                            </>
                          )}
                          <span className={`ml-auto px-2 py-0.5 rounded text-[9px] font-medium ${filterPhase === 'filtered' ? 'bg-primary-600 text-white' : 'bg-medical-100 text-medical-500'}`}>Filter</span>
                        </div>
                      </div>
                      {/* Filter bar */}
                      <div className="flex items-center gap-2 px-3 mb-1.5 text-[9px]">
                        <span className="font-semibold text-primary-600 flex items-center gap-1">&#x25A3; Narrative</span>
                        <span className="text-medical-500 flex items-center gap-1">&#x2581; All Events</span>
                        <span className="text-medical-200">|</span>
                        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />Key 22</span>
                        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Supporting 82</span>
                        <span className="flex items-center gap-1 text-medical-400 line-through"><span className="w-1.5 h-1.5 rounded-full bg-medical-300" />Routine 4</span>
                        <span className="flex items-center gap-1 text-medical-400 line-through"><span className="w-1.5 h-1.5 rounded-full bg-medical-200" />Sub-events</span>
                        <span className="text-medical-200">|</span>
                        <span className="text-[9px] text-medical-500 flex items-center gap-1">&#x21C5; Newest</span>
                        <span className="text-[9px] text-medical-400">Oldest</span>
                      </div>

                      {/* Content area — crossfades between full chronology and filtered results */}
                      <div className="flex-1 overflow-hidden relative">
                        {/* Full chronology (shown during idle/typing) */}
                        <div className="absolute inset-0 px-3 transition-all duration-500 overflow-hidden" style={{ opacity: filterPhase === 'idle' || filterPhase === 'typing' ? 1 : 0 }}>
                          {/* AT A GLANCE */}
                          <div className="p-2.5 bg-amber-50/70 rounded-lg border border-amber-200/60 mb-2">
                            <p className="text-[9px] font-bold text-amber-600 tracking-wide mb-1">AT A GLANCE</p>
                            <p className="text-[8.5px] text-medical-700 leading-relaxed">The claimant has a background of secondary progressive multiple sclerosis, diagnosed in 1992 and established as secondary progressive by 2009, resulting in frame-dependent mobility and ill-health retirement from nursing by 2010. On 11 November 2024, the claimant was involved in a road traffic collision on the A247 near Send, Surrey. A multi-region CT trauma series and facial X-rays were performed. Following overnight observation, the claimant was discharged on 12 November 2024.</p>
                          </div>
                          {/* Narrative sections */}
                          <div className="space-y-1.5">
                            <div>
                              <div className="flex items-center justify-between">
                                <p className="text-[10px] font-bold text-medical-900 flex items-center gap-1">
                                  <span className="text-medical-400 text-[8px]">&#9662;</span> 11 November 2024 &mdash; The index collision and acute hospital admission
                                </p>
                                <span className="text-[8px] text-medical-400 whitespace-nowrap">14 events</span>
                              </div>
                              <p className="text-[8.5px] text-medical-600 leading-relaxed mt-1 ml-3">On 11 November 2024, the claimant was involved in a road traffic collision on the A247 near Send, Surrey, sustaining a head injury with loss of consciousness. She was conveyed by ambulance to Royal Surrey County Hospital at 19:33 where a multi-region CT trauma series and facial X-rays were performed. Following overnight observation, the claimant was discharged on 12 November 2024.</p>
                            </div>
                            <div>
                              <div className="flex items-center justify-between">
                                <p className="text-[10px] font-bold text-medical-900 flex items-center gap-1">
                                  <span className="text-medical-400 text-[8px]">&#9662;</span> Late November 2024 to March 2025 &mdash; Post-discharge review and emerging complications
                                </p>
                                <span className="text-[8px] text-medical-400 whitespace-nowrap">10 events</span>
                              </div>
                              <p className="text-[8.5px] text-medical-600 leading-relaxed mt-1 ml-3">Following discharge, the claimant reported worsening mobility and new onset cervical pain. GP consultations documented increased spasticity in both lower limbs. A referral to neurology was initiated and physiotherapy was commenced for post-traumatic deconditioning.</p>
                            </div>
                            <div>
                              <div className="flex items-center justify-between">
                                <p className="text-[10px] font-bold text-medical-900 flex items-center gap-1">
                                  <span className="text-medical-400 text-[8px]">&#9662;</span> April 2025 to October 2025 &mdash; Ongoing recovery and longer-term follow-up
                                </p>
                                <span className="text-[8px] text-medical-400 whitespace-nowrap">10 events</span>
                              </div>
                              <p className="text-[8.5px] text-medical-600 leading-relaxed mt-1 ml-3">Outpatient neurology review confirmed no new demyelinating activity but documented progressive deterioration in mobility attributed to the superimposed musculoskeletal injuries. Physiotherapy was continued and an occupational therapy assessment for home adaptations was completed in August 2025.</p>
                            </div>
                          </div>
                        </div>

                        {/* Filtered results (shown briefly after typing, before citation opens) */}
                        <div className="absolute inset-0 px-3 transition-all duration-500 overflow-hidden" style={{ opacity: filterPhase === 'filtered' ? 1 : 0 }}>
                          <div className="p-2 bg-medical-50 rounded-lg border border-medical-200/50 mb-2.5">
                            <p className="text-[9px] font-medium text-medical-700 flex items-center gap-1">
                              <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" /></svg>
                              Filtered &mdash; showing 5 of 108 events
                            </p>
                            <p className="text-[8px] text-medical-500 mt-0.5">Early MS diagnosis and initial relapse events from 1992 to 1996</p>
                          </div>
                          <div className="space-y-2.5">
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                              <div>
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-[9px] font-medium">Diagnosis</span>
                                  <span className="text-[9px] text-medical-500">01/01/1992</span>
                                </div>
                                <p className="font-medium text-[10px] text-medical-900">Multiple Sclerosis Diagnosed; Optic Neuritis and Sensory Myelitis</p>
                                <p className="text-[9px] text-medical-700 mt-0.5 leading-relaxed">
                                  Multiple sclerosis diagnosed following <span className="border-b border-dotted border-amber-400">right optic neuritis</span><sup className="text-amber-500 text-[7px]">*</sup> occurring early in the year, followed later the same year by <span className="border-b border-dotted border-amber-400">sensory myelitis</span><sup className="text-amber-500 text-[7px]">*</sup>. Diagnosis confirmed after <span className="border-b border-dotted border-amber-400">MRI and VEP testing</span><sup className="text-amber-500 text-[7px]">*</sup>.
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                              <div>
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[9px] font-medium">Investigation</span>
                                  <span className="text-[9px] text-medical-500">03/11/1992</span>
                                </div>
                                <p className="font-medium text-[10px] text-medical-900">MRI Brain and Cervical Cord with Gadolinium</p>
                                <p className="text-[9px] text-medical-600">Dr Melissa Grant &mdash; Royal Woking Hospital, Radiology</p>
                                <p className="text-[9px] text-medical-700 mt-0.5 leading-relaxed">
                                  Multiple small ovoid high T2 signal <span className="border-b border-dotted border-amber-400">foci</span><sup className="text-amber-500 text-[7px]">*</sup> identified in the periventricular white matter. Imaging appearances compatible with <span className="border-b border-dotted border-amber-400">demyelinating disease</span><sup className="text-amber-500 text-[7px]">*</sup>.
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                              <div>
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <span className="px-1.5 py-0.5 bg-cyan-50 text-cyan-700 rounded text-[9px] font-medium">Treatment</span>
                                  <span className="text-[9px] text-medical-500">15/06/1993</span>
                                </div>
                                <p className="font-medium text-[10px] text-medical-900">Commenced on Interferon Beta-1a (Avonex)</p>
                                <p className="text-[9px] text-medical-600">Dr Melissa Grant &mdash; Royal Woking Hospital, Neurology</p>
                                <p className="text-[9px] text-medical-700 mt-0.5 leading-relaxed">
                                  Disease-modifying therapy initiated following two clinical relapses within 12 months<sup className="text-amber-500 text-[7px]">*</sup>. Weekly intramuscular injections commenced with baseline bloods and liver function monitoring<sup className="text-amber-500 text-[7px]">*</sup>.
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                              <div>
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[9px] font-medium">Clinical Event</span>
                                  <span className="text-[9px] text-medical-500">14/02/1994</span>
                                </div>
                                <p className="font-medium text-[10px] text-medical-900">Relapse &mdash; Sensory Symptoms Left Upper Limb</p>
                                <p className="text-[9px] text-medical-600">Dr Melissa Grant &mdash; Royal Woking Hospital, Neurology</p>
                                <p className="text-[9px] text-medical-700 mt-0.5 leading-relaxed">
                                  Acute onset <span className="border-b border-dotted border-amber-400">numbness and paraesthesiae</span><sup className="text-amber-500 text-[7px]">*</sup> in left hand extending to forearm over 48 hours. Treated with 3-day course of IV methylprednisolone<sup className="text-amber-500 text-[7px]">*</sup>. Symptoms partially resolved over 6 weeks<sup className="text-amber-500 text-[7px]">*</sup>.
                                </p>
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                              <div>
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[9px] font-medium">Investigation</span>
                                  <span className="text-[9px] text-medical-500">22/03/1994</span>
                                </div>
                                <p className="font-medium text-[10px] text-medical-900">Repeat MRI Brain with Gadolinium</p>
                                <p className="text-[9px] text-medical-600">Dr Melissa Grant &mdash; Royal Woking Hospital, Radiology</p>
                                <p className="text-[9px] text-medical-700 mt-0.5 leading-relaxed">
                                  Two new enhancing lesions identified in the <span className="border-b border-dotted border-amber-400">left periventricular white matter</span><sup className="text-amber-500 text-[7px]">*</sup>. Previous lesions unchanged. Findings consistent with <span className="border-b border-dotted border-amber-400">dissemination in time and space</span><sup className="text-amber-500 text-[7px]">*</sup>.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Citation view (slides in after filtered results) */}
                        <div className="absolute inset-0 transition-all duration-700 overflow-hidden flex" style={{ opacity: filterPhase === 'citation' ? 1 : 0 }}>
                          {/* Left: filtered events with one selected */}
                          <div className="flex-1 overflow-hidden p-2">
                            <div className="flex items-center gap-2 mb-1.5 text-[8px]">
                              <span className="font-semibold text-primary-600">Narrative</span>
                              <span className="text-medical-500">All Events</span>
                              <span className="text-medical-200">|</span>
                              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500" />Key 22</span>
                            </div>
                            <div className="p-1.5 bg-medical-50 rounded-lg border border-medical-200/50 mb-1.5">
                              <p className="text-[7px] font-medium text-medical-700 flex items-center gap-1">
                                <svg className="h-2 w-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" /></svg>
                                Filtered &mdash; showing 5 of 108 events
                              </p>
                            </div>
                            <div className="space-y-1.5">
                              {/* Event 1 — SELECTED (highlighted) */}
                              <div className="flex items-start gap-1.5 p-1.5 rounded-lg bg-primary-50/60 border border-primary-200 ring-1 ring-primary-300">
                                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                                <div>
                                  <div className="flex items-center gap-1 mb-0.5">
                                    <span className="px-1 py-0.5 bg-green-50 text-green-700 rounded text-[7px] font-medium">Diagnosis</span>
                                    <span className="text-[7px] text-medical-500">01/01/1992</span>
                                  </div>
                                  <p className="font-medium text-[8px] text-medical-900">Multiple Sclerosis Diagnosed; Optic Neuritis and Sensory Myelitis</p>
                                  <p className="text-[7px] text-medical-700 mt-0.5 leading-relaxed">
                                    Diagnosed following <span className="border-b border-dotted border-amber-400">right optic neuritis</span><sup className="text-amber-500 text-[6px]">*</sup>, followed by <span className="border-b border-dotted border-amber-400">sensory myelitis</span><sup className="text-amber-500 text-[6px]">*</sup>. Confirmed after <span className="border-b-2 border-amber-400 bg-amber-50 cursor-pointer">MRI and VEP testing</span><sup className="text-amber-500 text-[6px]">*</sup>.
                                  </p>
                                </div>
                              </div>
                              {/* Event 2 */}
                              <div className="flex items-start gap-1.5 px-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                                <div>
                                  <div className="flex items-center gap-1 mb-0.5">
                                    <span className="px-1 py-0.5 bg-purple-50 text-purple-700 rounded text-[7px] font-medium">Investigation</span>
                                    <span className="text-[7px] text-medical-500">03/11/1992</span>
                                  </div>
                                  <p className="font-medium text-[8px] text-medical-900">MRI Brain and Cervical Cord with Gadolinium</p>
                                  <p className="text-[7px] text-medical-600">Dr Melissa Grant &mdash; Royal Woking Hospital</p>
                                </div>
                              </div>
                              {/* Event 3 */}
                              <div className="flex items-start gap-1.5 px-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                                <div>
                                  <div className="flex items-center gap-1 mb-0.5">
                                    <span className="px-1 py-0.5 bg-cyan-50 text-cyan-700 rounded text-[7px] font-medium">Treatment</span>
                                    <span className="text-[7px] text-medical-500">15/06/1993</span>
                                  </div>
                                  <p className="font-medium text-[8px] text-medical-900">Commenced on Interferon Beta-1a (Avonex)</p>
                                </div>
                              </div>
                              {/* Event 4 */}
                              <div className="flex items-start gap-1.5 px-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5 flex-shrink-0" />
                                <div>
                                  <div className="flex items-center gap-1 mb-0.5">
                                    <span className="px-1 py-0.5 bg-amber-50 text-amber-700 rounded text-[7px] font-medium">Clinical Event</span>
                                    <span className="text-[7px] text-medical-500">14/02/1994</span>
                                  </div>
                                  <p className="font-medium text-[8px] text-medical-900">Relapse &mdash; Sensory Symptoms Left Upper Limb</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Right: Source document sidebar — slides in */}
                          <div className="bg-medical-50/30 flex flex-col overflow-hidden transition-all duration-700" style={{ width: filterPhase === 'citation' ? '44%' : '0%', opacity: filterPhase === 'citation' ? 1 : 0 }}>
                            <div className="px-2 py-1.5 bg-white border-b border-medical-100 flex items-center justify-between">
                              <div className="flex items-center gap-1">
                                <FileText className="h-3 w-3 text-primary-500" />
                                <span className="text-[9px] font-semibold text-medical-900 whitespace-nowrap">Source Document</span>
                              </div>
                              <span className="text-medical-400 text-sm cursor-pointer">&times;</span>
                            </div>
                            <div className="px-2 py-1.5 bg-white border-b border-medical-100">
                              <p className="text-[9px] font-medium text-medical-900 whitespace-nowrap">Neurology Outpatient Letter</p>
                              <p className="text-[8px] text-medical-600 whitespace-nowrap">Royal Woking Hospital &middot; 01/01/1992</p>
                              <p className="text-[8px] text-medical-500">Page 8 of 47</p>
                            </div>
                            <div className="flex-1 overflow-hidden bg-[#faf9f6] p-1.5" style={{ fontFamily: 'monospace' }}>
                              <div className="border border-medical-300 bg-white">
                                <div className="bg-medical-200/60 px-1.5 py-0.5 border-b border-medical-300">
                                  <p className="text-[6.5px] font-bold text-medical-800 uppercase">Neurology Clinic Letter</p>
                                </div>
                                <div className="divide-y divide-medical-200 text-[6.5px] text-medical-700">
                                  <div className="flex">
                                    <span className="w-[35%] px-1 py-0.5 bg-medical-50 font-semibold border-r border-medical-200">Date:</span>
                                    <span className="flex-1 px-1 py-0.5">01/01/1992</span>
                                  </div>
                                  <div className="flex">
                                    <span className="w-[35%] px-1 py-0.5 bg-medical-50 font-semibold border-r border-medical-200">Consultant:</span>
                                    <span className="flex-1 px-1 py-0.5">Dr M Grant</span>
                                  </div>
                                  <div className="flex">
                                    <span className="w-[35%] px-1 py-0.5 bg-medical-50 font-semibold border-r border-medical-200">Referral:</span>
                                    <span className="flex-1 px-1 py-0.5">GP &mdash; ?demyelination</span>
                                  </div>
                                  <div className="bg-medical-200/60 px-1.5 py-0.5">
                                    <p className="font-bold text-medical-800 uppercase text-[6.5px]">History</p>
                                  </div>
                                  <div className="px-1 py-0.5">
                                    <p>This 24-year-old nurse presents with a 6-month history of visual disturbance in the right eye followed by sensory symptoms in the lower limbs.</p>
                                  </div>
                                  <div className="bg-medical-200/60 px-1.5 py-0.5">
                                    <p className="font-bold text-medical-800 uppercase text-[6.5px]">Investigations</p>
                                  </div>
                                  <div className="px-1 py-0.5">
                                    <p><span className="bg-amber-100 px-0.5">MRI brain: multiple periventricular white matter lesions.</span></p>
                                    <p><span className="bg-amber-100 px-0.5">VEP: delayed P100 right eye.</span></p>
                                    <p>CSF: oligoclonal bands positive.</p>
                                  </div>
                                  <div className="bg-medical-200/60 px-1.5 py-0.5">
                                    <p className="font-bold text-medical-800 uppercase text-[6.5px]">Impression</p>
                                  </div>
                                  <div className="px-1 py-0.5">
                                    <p>Clinically definite multiple sclerosis. Relapsing-remitting pattern. Will discuss DMT options.</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Screen 3: Report drafting */}
                    <div className="absolute inset-0 transition-all duration-500 ease-in-out overflow-hidden flex flex-col" style={{ opacity: activeScreen === 3 ? 1 : 0, transform: activeScreen === 3 ? 'translateX(0)' : 'translateX(20px)' }}>
                      {/* Top nav tabs */}
                      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-medical-100 overflow-x-auto text-[8px] text-medical-500 whitespace-nowrap">
                        {['Records', 'Overview', 'Claimant', 'Chronology', 'Expert Evidence', 'Images', 'Body Map', 'Clinical Standards', 'Evidence Integrity', 'Causation'].map((tab) => (
                          <span key={tab} className={`px-1.5 py-0.5 ${tab === 'Expert Evidence' ? 'text-primary-600 font-semibold border-b-2 border-primary-500' : 'text-medical-500'}`}>{tab}</span>
                        ))}
                      </div>
                      <div className="flex-1 px-4 py-3 overflow-hidden relative">
                        {/* Options view — fades out when drafting */}
                        <div className="absolute inset-0 px-4 py-3 transition-all duration-500" style={{ opacity: reportPhase === 'drafting' ? 0 : 1 }}>
                          <div className="flex items-center gap-1.5 mb-3">
                            <Zap className="w-3.5 h-3.5 text-primary-500" />
                            <p className="text-[11px] font-bold text-medical-900">Draft a new report</p>
                          </div>
                          <div className="space-y-2">
                            <div className="p-3 rounded-xl border-2 border-amber-300 bg-amber-50/50 flex items-center gap-3 cursor-pointer">
                              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                                <Star className="w-5 h-5 text-amber-600" />
                              </div>
                              <div className="flex-1">
                                <p className="text-[10px] font-bold text-amber-800">Key Findings Report</p>
                                <p className="text-[9px] text-amber-600">Synthesise the starred findings into a structured report</p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-amber-400" />
                            </div>
                            {/* Chronology Summary — gets selected */}
                            <div className={`p-3 rounded-xl flex items-center gap-3 cursor-pointer transition-all duration-500 ${reportPhase === 'selected' ? 'border-2 border-primary-400 bg-primary-50 ring-2 ring-primary-200 scale-[1.02]' : 'border border-primary-200 bg-primary-50/30'}`}>
                              <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                                <Clock className="w-5 h-5 text-primary-600" />
                              </div>
                              <div className="flex-1">
                                <p className="text-[10px] font-bold text-medical-900">Chronology Summary</p>
                                <p className="text-[9px] text-primary-600">Structured chronological narrative of the medical records</p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-primary-300" />
                            </div>
                            <div className="p-3 rounded-xl border border-dashed border-medical-200 bg-white flex items-center gap-3 cursor-pointer">
                              <div className="w-9 h-9 rounded-lg bg-medical-100 flex items-center justify-center flex-shrink-0">
                                <ArrowRight className="w-5 h-5 text-medical-500 -rotate-45" />
                              </div>
                              <div className="flex-1">
                                <p className="text-[10px] font-bold text-medical-900">Mirror a previous report</p>
                                <p className="text-[9px] text-medical-500">Upload a Word report to create a reusable template</p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-medical-300" />
                            </div>
                            <div className="p-3 rounded-xl border border-dashed border-medical-200 bg-white flex items-center gap-3 cursor-pointer">
                              <div className="w-9 h-9 rounded-lg bg-medical-100 flex items-center justify-center flex-shrink-0">
                                <FileText className="w-5 h-5 text-medical-500" />
                              </div>
                              <div className="flex-1">
                                <p className="text-[10px] font-bold text-medical-900">Use a template...</p>
                                <p className="text-[9px] text-medical-500">Browse specialism-specific report templates</p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-medical-300" />
                            </div>
                          </div>
                        </div>

                        {/* Drafting view — fades in with streaming text */}
                        <div className="absolute inset-0 px-4 py-3 transition-all duration-500 overflow-hidden" style={{ opacity: reportPhase === 'drafting' ? 1 : 0 }}>
                          {/* Generating header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-primary-500" />
                              <p className="text-[11px] font-bold text-medical-900">Chronology Summary</p>
                            </div>
                            <span className="px-2 py-0.5 bg-primary-50 text-primary-700 text-[8px] font-medium rounded-full flex items-center gap-1">
                              <Zap className="w-2.5 h-2.5 text-primary-500 animate-pulse" />
                              Generating...
                            </span>
                          </div>
                          {/* Streaming report content */}
                          <div className="space-y-2.5 text-[9px] text-medical-700 leading-relaxed">
                            {(() => {
                              let charsUsed = 0;
                              return reportSections.map((section, idx) => {
                                const sectionText = section.heading + section.body;
                                const sectionLen = sectionText.length;
                                const charsAvailable = Math.max(0, reportGenChars - charsUsed);
                                charsUsed += sectionLen;
                                if (charsAvailable <= 0) return null;
                                const headingLen = section.heading.length;
                                const headingVisible = Math.min(charsAvailable, headingLen);
                                const bodyChars = Math.max(0, charsAvailable - headingLen);
                                const bodyVisible = section.body.substring(0, bodyChars);
                                const isLastVisible = charsAvailable < sectionLen;
                                return (
                                  <div key={idx}>
                                    <p className="text-[8px] font-bold text-medical-500 uppercase tracking-wide mb-1">
                                      {section.heading.substring(0, headingVisible)}
                                    </p>
                                    {bodyChars > 0 && (
                                      <p>
                                        {bodyVisible}
                                        {isLastVisible && (
                                          <span className="inline-block w-[3px] h-[10px] bg-primary-500 ml-[1px] animate-pulse rounded-sm align-text-bottom" />
                                        )}
                                      </p>
                                    )}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                          {/* Progress bar */}
                          <div className="absolute bottom-3 left-4 right-4">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-0.5 bg-medical-100 rounded-full overflow-hidden">
                                <div className="h-full bg-primary-400 rounded-full transition-all duration-100" style={{ width: `${Math.min(100, (reportGenChars / reportTotalChars) * 100)}%` }} />
                              </div>
                              <span className="text-[7px] text-medical-400 font-mono">{Math.min(100, Math.round((reportGenChars / reportTotalChars) * 100))}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                  {/* Screen 4: Features — staggered reveal grid */}
                  <div className="absolute inset-0 transition-opacity duration-500 flex flex-col" style={{ opacity: activeScreen === 4 ? 1 : 0, pointerEvents: activeScreen === 4 ? 'auto' : 'none' }}>
                    <div className="flex-1 px-5 py-4 flex flex-col justify-center">
                      <div className="grid grid-cols-3 gap-2.5 w-full">
                        {featureTiles.map((tile, i) => {
                          const visible = i < featuresRevealed;
                          return (
                            <div
                              key={i}
                              className="flex flex-col items-center text-center p-4 rounded-xl border border-medical-200/60 bg-white/80 transition-all duration-500"
                              style={{
                                opacity: visible ? 1 : 0,
                                transform: visible ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(12px)',
                              }}
                            >
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2.5 ${tile.colour === 'amber' ? 'bg-amber-50' : 'bg-primary-50'}`}>
                                <tile.icon className={`w-5 h-5 ${tile.colour === 'amber' ? 'text-amber-600' : 'text-primary-600'}`} />
                              </div>
                              <p className="text-[10px] font-bold text-medical-900 leading-tight mb-1">{tile.label}</p>
                              <p className="text-[8px] text-medical-500 leading-snug">{tile.desc}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  </div>

                  {/* Bottom: caption with subtle chevrons */}
                  <div className="px-4 py-3.5 border-t border-primary-200 bg-primary-50/70">
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={() => setActiveScreen((prev) => (prev - 1 + screenCount) % screenCount)}
                        className="text-medical-300 hover:text-medical-500 transition-colors"
                      >
                        <ChevronRight className="w-5 h-5 rotate-180" />
                      </button>
                      <p className="text-sm text-medical-700 text-center font-medium min-h-[20px] transition-all duration-300">
                        {screenCaptions[activeScreen]}
                      </p>
                      <button
                        onClick={() => setActiveScreen((prev) => (prev + 1) % screenCount)}
                        className="text-medical-300 hover:text-medical-500 transition-colors"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
                {/* Decorative glow */}
                <div className="absolute -inset-4 bg-gradient-to-br from-primary-100/20 to-primary-50/10 rounded-3xl blur-2xl -z-10" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white/50 to-primary-50/30">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold text-medical-900 mb-4 font-display text-center">
              Simple, Transparent Pricing
            </h2>
            <p className="text-medical-600 text-center mb-12 max-w-2xl mx-auto">
              Two-part pricing: a small monthly subscription unlocks the platform, and you only pay for the pages you actually process. No per-seat fees. No annual lock-in.
            </p>
          </AnimatedSection>

          <AnimatedSection>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-primary-200/60 shadow-lg shadow-primary-100/30 p-6 sm:p-10">
              <div className="text-center mb-8">
                <span className="inline-block text-xs font-semibold tracking-wider uppercase text-primary-700 bg-primary-50 px-3 py-1 rounded-full">How pricing works</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 relative">
                {/* Step 1: Subscribe */}
                <div className="relative text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start mb-3">
                    <div className="w-9 h-9 rounded-full bg-primary-600 text-white font-bold text-sm flex items-center justify-center shadow-md shadow-primary-600/30">1</div>
                    <CreditCard className="h-5 w-5 text-primary-600 ml-3" />
                  </div>
                  <h4 className="text-base font-bold text-medical-900 mb-1">Subscribe</h4>
                  <div className="flex items-baseline justify-center md:justify-start mb-2">
                    <span className="text-3xl font-bold text-primary-600">£24.99</span>
                    <span className="text-medical-500 ml-1 text-sm">/month + VAT</span>
                  </div>
                  <p className="text-sm text-medical-600">Unlocks the platform — accounts, dashboards, all report types and AI features. Cancel any time.</p>

                  <div className="hidden md:flex absolute top-3 -right-2 text-medical-300 z-10">
                    <ChevronRight className="h-6 w-6" />
                  </div>
                </div>

                {/* Step 2: Bonus pages */}
                <div className="relative text-center md:text-left md:pl-4">
                  <div className="flex items-center justify-center md:justify-start mb-3">
                    <div className="w-9 h-9 rounded-full bg-amber-500 text-white font-bold text-sm flex items-center justify-center shadow-md shadow-amber-500/30">2</div>
                    <Gift className="h-5 w-5 text-amber-600 ml-3" />
                  </div>
                  <h4 className="text-base font-bold text-medical-900 mb-1">Get 1,000 bonus pages</h4>
                  <div className="flex items-baseline justify-center md:justify-start mb-2">
                    <span className="text-3xl font-bold text-amber-600">Free</span>
                    <span className="text-medical-500 ml-1 text-sm">on signup</span>
                  </div>
                  <p className="text-sm text-medical-600">Your first 1,000 pages of processing are included — enough for a typical multi-document case.</p>

                  <div className="hidden md:flex absolute top-3 -right-2 text-medical-300 z-10">
                    <ChevronRight className="h-6 w-6" />
                  </div>
                </div>

                {/* Step 3: Per-page after bonus */}
                <div className="text-center md:text-left md:pl-4">
                  <div className="flex items-center justify-center md:justify-start mb-3">
                    <div className="w-9 h-9 rounded-full bg-teal-600 text-white font-bold text-sm flex items-center justify-center shadow-md shadow-teal-600/30">3</div>
                    <TrendingUp className="h-5 w-5 text-teal-600 ml-3" />
                  </div>
                  <h4 className="text-base font-bold text-medical-900 mb-1">Pay per page after that</h4>
                  <div className="flex items-baseline justify-center md:justify-start mb-2">
                    <span className="text-3xl font-bold text-teal-600">from 21p</span>
                    <span className="text-medical-500 ml-1 text-sm">/page</span>
                  </div>
                  <p className="text-sm text-medical-600">Only what you actually process. Volume discounts apply automatically — see tiers below.</p>
                </div>
              </div>

              {/* Per-page tier table */}
              <div className="mt-10 pt-8 border-t border-medical-100">
                <p className="text-xs font-semibold tracking-wider uppercase text-medical-500 text-center mb-4">Per-page rates (after the 1,000 bonus pages)</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
                  {[
                    { range: '1 – 1,500', rate: '30p', label: null },
                    { range: '1,501 – 5,000', rate: '27p', label: '10% off' },
                    { range: '5,001 – 10,000', rate: '24p', label: '20% off' },
                    { range: '10,001+', rate: '21p', label: '30% off' },
                  ].map((tier) => (
                    <div key={tier.range} className="bg-white rounded-lg border border-medical-100 p-3 text-center">
                      <div className="text-xs text-medical-500 mb-1">{tier.range} pages</div>
                      <div className="text-lg font-bold text-medical-900">{tier.rate}<span className="text-xs text-medical-500 font-normal"> /page</span></div>
                      {tier.label && <div className="text-xs text-teal-700 font-medium mt-1">{tier.label}</div>}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-medical-500 text-center mt-4">Billed monthly based on pages processed. Tiers reset each calendar month. Per-case VAT invoices on completion.</p>
              </div>

              <div className="mt-10 text-center">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center px-8 py-3.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all duration-300 shadow-md shadow-primary-600/20"
                >
                  Get Started — Includes 1,000 Bonus Pages <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-primary-50/40 to-white/50">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-medical-900 mb-4 font-display">
                Built for Medico-Legal Professionals
              </h2>
              <p className="text-lg text-medical-600 max-w-2xl mx-auto">
                Designed specifically for professionals managing complex medical evidence in litigation.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Scale,
                title: 'Panel Solicitors',
                headline: 'Faster liability assessment and LoR preparation',
                body: 'Have a structured chronology and initial liability picture ready minutes after uploading records. Your team can assess merit and start drafting the Letter of Response instead of spending weeks on manual review.',
                iconColor: 'text-cyan-600',
                iconBg: 'from-cyan-50 to-cyan-100',
              },
              {
                icon: Users,
                title: 'Claimant Solicitors',
                headline: 'Earlier breach identification, stronger claims',
                body: 'Potential breaches and causation links are visible at case intake, not weeks into the file. Instruct experts with structured, cited summaries so they can focus on the issues that matter.',
                iconColor: 'text-teal-600',
                iconBg: 'from-teal-50 to-teal-100',
              },
              {
                icon: GraduationCap,
                title: 'Medical Experts',
                headline: 'More time on clinical opinion, less on pagination',
                body: 'Start with a structured chronology and evidence organised by theme instead of raw records. Navigate directly to source documents from any finding, so the preparatory work is done before you begin writing.',
                iconColor: 'text-purple-600',
                iconBg: 'from-purple-50 to-purple-100',
              },
            ].map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 150}>
                <div className="relative bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-medical-200/50 hover:border-primary-300/50 transition-all duration-500 hover:shadow-xl hover:shadow-primary-100/20 h-full">
                  <div className={`w-12 h-12 bg-gradient-to-br ${item.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                    <item.icon className={`h-6 w-6 ${item.iconColor}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-medical-900 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-primary-700 font-medium text-sm mb-3">
                    {item.headline}
                  </p>
                  <p className="text-medical-600 text-sm leading-relaxed">
                    {item.body}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Problem → Solution Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white/50 to-primary-50/20">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-medical-900 mb-4 font-display">
                Clinical Negligence Litigation Is Slowed by Manual Record Review
              </h2>
              <p className="text-lg text-medical-600 max-w-3xl mx-auto">
                Starting with thousands of pages of unstructured medical records, manual review
                creates delays in chronology preparation, inconsistent issue identification,
                high internal costs, and slower expert instruction. ClinLexis
                analyses medical records and produces structured, cited outputs designed for
                PI and clinical negligence workflows.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <FeatureCard
              icon={Clock}
              title="Structured Chronologies"
              description="Clear, dated medical timelines generated from the full record set, with every entry linked back to its source document."
              delay={0}
              iconColor="text-cyan-600"
              iconBg="from-cyan-50 to-cyan-100"
            />
            <FeatureCard
              icon={Scale}
              title="Breach & Causation Mapping"
              description="Key clinical decisions, delays, omissions, and potential causative events surfaced and mapped across the medical record."
              delay={150}
              iconColor="text-purple-600"
              iconBg="from-purple-50 to-purple-100"
            />
            <FeatureCard
              icon={Search}
              title="Evidence Navigation"
              description="Navigate directly to the supporting record behind any finding. Full citation linking throughout."
              delay={300}
              iconColor="text-teal-600"
              iconBg="from-teal-50 to-teal-100"
            />
            <FeatureCard
              icon={FileText}
              title="Expert-Ready Summaries"
              description="Structured briefs with key issues identified, ready to send with the letter of instruction."
              delay={450}
              iconColor="text-orange-600"
              iconBg="from-orange-50 to-orange-100"
            />
          </div>
        </div>
      </section>

      {/* Output Demo Section */}
      <section id="output-demo" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-primary-50/20 to-white/50">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-medical-900 mb-4 font-display">
                See What ClinLexis Produces
              </h2>
              <p className="text-lg text-medical-600 max-w-2xl mx-auto">
                Structured outputs designed for real litigation workflows.
              </p>
            </div>
          </AnimatedSection>

          <div className="space-y-16">

            {/* 1. Chronology with AI Filtering */}
            <div ref={demoRef}>
              <AnimatedSection>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
                  <div className="lg:col-span-2 lg:sticky lg:top-32">
                    <div className="inline-flex items-center px-3 py-1 bg-cyan-50 rounded-full text-xs font-medium text-cyan-600 mb-3">Chronology</div>
                    <h3 className="text-2xl font-bold text-medical-900 mb-3 font-display">AI-powered narrative chronology</h3>
                    <p className="text-medical-600 text-sm leading-relaxed mb-4">
                      108 events extracted, classified, and organised into a narrative timeline. Filter by natural language to find exactly what you need across the full record set.
                    </p>
                    <ul className="space-y-2 text-sm text-medical-600">
                      <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>Events classified as Key, Supporting, or Routine</span></li>
                      <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>Natural language search across the full timeline</span></li>
                      <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>Every entry linked to its source document</span></li>
                    </ul>
                  </div>
                  <div className="lg:col-span-3">
                    <div className="bg-white rounded-2xl shadow-xl border border-medical-200/50 overflow-hidden">
                      {/* Search bar with typing animation */}
                      <div className="p-3 border-b border-medical-100">
                        <div className="flex items-center bg-medical-50 rounded-lg px-3 py-2">
                          <Search className="h-4 w-4 text-medical-400 mr-2" />
                          {demoFilterPhase === 'full' ? (
                            <span className="text-medical-400 text-sm">104 of 108 events &mdash; ask anything, e.g. &ldquo;show only surgical events&rdquo;</span>
                          ) : (
                            <>
                              <span className="text-medical-900 text-sm">{filterQuery.slice(0, demoTypedChars)}</span>
                              {demoFilterPhase === 'typing' && <span className="w-0.5 h-4 bg-primary-500 animate-pulse ml-0.5" />}
                              {demoFilterPhase === 'filtered' && (
                                <div className="ml-auto">
                                  <span className="px-2.5 py-1 bg-primary-600 text-white rounded text-xs font-medium">Filter</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {/* Filter bar */}
                      <div className="px-4 py-2.5 border-b border-medical-100 flex items-center gap-3 text-xs flex-wrap">
                        <span className="font-medium text-primary-600">Narrative</span>
                        <span className="text-medical-400">All Events</span>
                        <span className="text-medical-300">|</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Key 22</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />Supporting 82</span>
                        <span className="flex items-center gap-1 text-medical-400 line-through"><span className="w-2 h-2 rounded-full bg-medical-300" />Routine 4</span>
                      </div>

                      {/* Content: crossfade between full chronology and filtered */}
                      <div className="relative" style={{ minHeight: '340px' }}>
                        {/* Full chronology view */}
                        <div className="absolute inset-0 p-4 transition-all duration-500" style={{ opacity: demoFilterPhase === 'filtered' ? 0 : 1, pointerEvents: demoFilterPhase === 'filtered' ? 'none' : 'auto' }}>
                          {/* AT A GLANCE */}
                          <div className="p-3 bg-amber-50/60 rounded-lg border border-amber-200/60 mb-4">
                            <p className="text-xs font-bold text-amber-700 mb-1">AT A GLANCE</p>
                            <p className="text-xs text-medical-700 leading-relaxed">The claimant has a background of secondary progressive multiple sclerosis, diagnosed in 1992 and established as secondary progressive by 2009, resulting in frame-dependent mobility and ill-health retirement from nursing by 2010. On 11 November 2024, the claimant was involved in a road traffic collision on the A247 near Send, Surrey, sustaining a left frontoparietal subdural haematoma, a right parietal linear fracture, and a severe right hip abductor injury.</p>
                          </div>
                          {/* Collapsible sections */}
                          <div className="space-y-3">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-sm font-semibold text-medical-900 flex items-center gap-1.5">
                                  <span className="text-medical-400">&#9662;</span> 11 November 2024 &mdash; Index collision and acute hospital admission
                                </p>
                                <span className="text-xs text-medical-400">14 events</span>
                              </div>
                              <div className="ml-4 h-1.5 w-full bg-medical-100 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-red-400 via-amber-400 to-amber-300 rounded-full" style={{ width: '65%' }} />
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-semibold text-medical-900 flex items-center gap-1.5">
                                <span className="text-medical-400">&#9656;</span> 12&ndash;27 November 2024 &mdash; Inpatient admission and management
                              </p>
                              <span className="text-xs text-medical-400">14 events</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-semibold text-medical-900 flex items-center gap-1.5">
                                <span className="text-medical-400">&#9656;</span> Late November 2024 to March 2025 &mdash; Post-discharge review
                              </p>
                              <span className="text-xs text-medical-400">10 events</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-semibold text-medical-900 flex items-center gap-1.5">
                                <span className="text-medical-400">&#9656;</span> April 2025 to October 2026 &mdash; Ongoing recovery and follow-up
                              </p>
                              <span className="text-xs text-medical-400">10 events</span>
                            </div>
                          </div>
                        </div>

                        {/* Filtered results */}
                        <div className="absolute inset-0 p-4 transition-all duration-500" style={{ opacity: demoFilterPhase === 'filtered' ? 1 : 0, pointerEvents: demoFilterPhase !== 'filtered' ? 'none' : 'auto' }}>
                          {/* Filter notice */}
                          <div className="p-3 bg-medical-50 rounded-lg border border-medical-200/50 flex items-start justify-between mb-4">
                            <div>
                              <p className="text-sm font-medium text-medical-900 flex items-center gap-1.5">
                                <svg className="h-3.5 w-3.5 text-medical-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" /></svg>
                                Filtered &mdash; showing 5 of 108 events
                              </p>
                              <p className="text-xs text-medical-500 mt-0.5">Early MS diagnosis and initial relapse events from 1992 to 1996</p>
                            </div>
                            <span className="text-medical-400 text-sm cursor-pointer">&times;</span>
                          </div>
                          {/* Events */}
                          <div className="space-y-4">
                            <div className="flex items-start gap-3">
                              <div className="w-2.5 h-2.5 rounded-full bg-cyan-500 mt-2 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 text-xs flex-wrap">
                                  <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs font-medium">Diagnosis</span>
                                  <span className="text-medical-500">01/01/1992</span>
                                </div>
                                <p className="font-medium text-sm text-medical-900">Multiple Sclerosis Diagnosed; Optic Neuritis and Sensory Myelitis</p>
                                <p className="text-xs text-medical-600 mt-1 leading-relaxed">Multiple sclerosis diagnosed following <span className="border-b border-dotted border-amber-400">right optic neuritis</span><sup className="text-amber-500">*</sup> occurring early in the year, followed later the same year by <span className="border-b border-dotted border-amber-400">sensory myelitis</span><sup className="text-amber-500">*</sup>. Diagnosis confirmed after <span className="border-b border-dotted border-amber-400">MRI and VEP testing</span><sup className="text-amber-500">*</sup>.</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="w-2.5 h-2.5 rounded-full bg-cyan-500 mt-2 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 text-xs flex-wrap">
                                  <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs font-medium">Investigation</span>
                                  <span className="text-medical-500">03/11/1992</span>
                                </div>
                                <p className="font-medium text-sm text-medical-900">MRI Brain and Cervical Cord with Gadolinium</p>
                                <p className="text-xs text-medical-500 mt-0.5">Dr Melissa Grant &mdash; Royal Woking Hospital, Radiology</p>
                                <p className="text-xs text-medical-600 mt-1 leading-relaxed"><span className="border-b border-dotted border-amber-400">Multiple small ovoid high T2 signal foci</span><sup className="text-amber-500">*</sup> identified in the periventricular white matter. Imaging appearances <span className="border-b border-dotted border-amber-400">compatible with demyelinating disease</span><sup className="text-amber-500">*</sup>.</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>

            {/* 2. Case Overview */}
            <AnimatedSection>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
                <div className="lg:col-span-3 order-2 lg:order-1">
                  <div className="bg-white rounded-2xl shadow-xl border border-medical-200/50 overflow-hidden">
                    {/* Tab bar */}
                    <div className="px-4 py-2.5 border-b border-medical-100 flex items-center gap-4 text-xs overflow-x-auto">
                      <span className="text-medical-400 whitespace-nowrap">Records</span>
                      <span className="text-primary-600 font-medium whitespace-nowrap border-b-2 border-primary-500 pb-2">Overview</span>
                      <span className="text-medical-400 whitespace-nowrap">Claimant</span>
                      <span className="text-medical-400 whitespace-nowrap">Chronology</span>
                      <span className="text-medical-400 whitespace-nowrap">Expert Evidence</span>
                      <span className="text-medical-400 whitespace-nowrap">Clinical Standards</span>
                      <span className="text-medical-400 whitespace-nowrap">Causation</span>
                    </div>
                    {/* Content */}
                    <div className="p-5 space-y-4">
                      {/* Case Overview card */}
                      <div className="p-4 bg-medical-50/50 rounded-xl border border-medical-100">
                        <p className="text-xs font-medium text-medical-900 mb-2 flex items-center gap-1.5">
                          <span className="text-primary-500">&#10022;</span> Case Overview
                        </p>
                        <p className="text-xs text-medical-600 leading-relaxed">
                          Jane Doe, born 01/05/1965, is a former staff nurse who was medically retired in 2010 owing to progressive disability from longstanding secondary progressive multiple sclerosis<sup className="text-amber-500">*</sup>. On 11 November 2024, she was involved in a rear-end road traffic collision on the A247 near Send, Surrey<sup className="text-amber-500">*</sup>. She sustained a small acute left frontoparietal subdural haematoma, a non-depressed linear fracture of the right parietal bone, acute non-displaced spinous process fractures at C7, T1, and T2<sup className="text-amber-500">*</sup>.
                        </p>
                      </div>
                      {/* Index Event card */}
                      <div className="p-4 bg-medical-50/50 rounded-xl border border-medical-100">
                        <p className="text-xs font-medium text-medical-900 mb-2 flex items-center gap-1.5">
                          <span className="text-amber-500">&#9888;</span> The Index Event
                        </p>
                        <p className="text-xs text-medical-600 leading-relaxed">
                          On 11 November 2024 at approximately 15:02 hours, Jane Doe was involved in a rear-end road traffic collision on the A247 near Send, Surrey, in which her stationary vehicle was struck from behind at a reported speed of approximately 35 mph<sup className="text-amber-500">*</sup>. A brief loss of consciousness of approximately three to four minutes was reported by witnesses<sup className="text-amber-500">*</sup>. Her initial Glasgow Coma Scale score at 15:10 was 13<sup className="text-amber-500">*</sup>.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2 order-1 lg:order-2 lg:sticky lg:top-32">
                  <div className="inline-flex items-center px-3 py-1 bg-teal-50 rounded-full text-xs font-medium text-teal-600 mb-3">Case Analysis</div>
                  <h3 className="text-2xl font-bold text-medical-900 mb-3 font-display">Structured case overview in seconds</h3>
                  <p className="text-medical-600 text-sm leading-relaxed mb-4">
                    Every case gets a complete overview synthesised from all uploaded documents. The index event, claimant background, medical history, and key findings are extracted and organised automatically.
                  </p>
                  <ul className="space-y-2 text-sm text-medical-600">
                    <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>Nine analysis tabs covering the full medico-legal picture</span></li>
                    <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>All findings cited back to source documents</span></li>
                    <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>Editable sections for human review and annotation</span></li>
                  </ul>
                </div>
              </div>
            </AnimatedSection>

            {/* 3. AI Assistant */}
            <AnimatedSection>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
                <div className="lg:col-span-2 lg:sticky lg:top-32">
                  <div className="inline-flex items-center px-3 py-1 bg-purple-50 rounded-full text-xs font-medium text-purple-600 mb-3">AI Assistant</div>
                  <h3 className="text-2xl font-bold text-medical-900 mb-3 font-display">Ask anything about your case</h3>
                  <p className="text-medical-600 text-sm leading-relaxed mb-4">
                    Query the full case record in natural language. The assistant searches across all uploaded documents, examines the chronology, and produces structured answers with citations.
                  </p>
                  <ul className="space-y-2 text-sm text-medical-600">
                    <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>Full access to every document in the case</span></li>
                    <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>Structured responses with clinical detail</span></li>
                    <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>Transparent tool use showing what was searched</span></li>
                  </ul>
                </div>
                <div className="lg:col-span-3">
                  <div className="bg-white rounded-2xl shadow-xl border border-medical-200/50 overflow-hidden">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-medical-100 flex items-center gap-2">
                      <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                        <span className="text-primary-600 text-xs font-bold">CL</span>
                      </div>
                      <span className="font-semibold text-sm text-medical-900">ClinLexis Assistant</span>
                      <span className="px-1.5 py-0.5 bg-green-50 text-green-600 rounded text-xs font-medium">Ask</span>
                    </div>
                    <div className="p-4 space-y-4">
                      {/* User message */}
                      <div className="flex justify-end">
                        <div className="bg-medical-50 rounded-xl rounded-tr-sm px-4 py-2.5 max-w-[70%]">
                          <p className="text-sm text-medical-900">Summarise the key events</p>
                          <p className="text-xs text-medical-400 mt-1">11:33</p>
                        </div>
                      </div>
                      {/* Assistant response */}
                      <div className="space-y-2">
                        {/* Tool use indicator */}
                        <div className="flex items-center gap-1.5 text-xs text-medical-400">
                          <div className="w-6 h-6 bg-primary-50 rounded-md flex items-center justify-center">
                            <span className="text-primary-500 text-xs">&#10003;</span>
                          </div>
                          <span>Assessed case profile, Examined timeline, ge...</span>
                        </div>
                        <p className="text-xs text-medical-500 mb-2">Case Summary &mdash; Jane Doe (Personal Injury)</p>
                        {/* Response content */}
                        <div className="text-sm text-medical-800 space-y-3">
                          <p className="font-bold text-medical-900">Background</p>
                          <p className="text-xs leading-relaxed text-medical-600">Jane Doe (DOB: 01/05/1965) is a former staff nurse who was medically retired in 2010 due to secondary progressive multiple sclerosis (SPMS). She lives alone in Woking, Surrey, with support from her adult daughter.</p>
                          <p className="font-bold text-medical-900">Pre-Existing Medical History</p>
                          <ul className="text-xs text-medical-600 space-y-1 list-disc pl-4">
                            <li>1992 &mdash; MS diagnosed following right optic neuritis and sensory myelitis</li>
                            <li>1996&ndash;2008 &mdash; Multiple relapses (brainstem, left leg weakness, sensory-motor, cerebellar)</li>
                            <li>2009 &mdash; Secondary progressive MS formally established</li>
                            <li>2010 &mdash; Ill-health retirement from nursing</li>
                          </ul>
                          <p className="font-bold text-medical-900">The Index Event &mdash; 11 November 2024</p>
                          <p className="text-xs leading-relaxed text-medical-600">Jane Doe was involved in a rear-end road traffic collision on the A247 near Send, Surrey, at approximately 15:02 hrs. She had a brief loss of consciousness (~3&ndash;4 minutes) and was amnesic for the impact<sup className="text-amber-500">*</sup>.</p>
                        </div>
                      </div>
                    </div>
                    <div className="h-4 bg-gradient-to-t from-white to-transparent" />
                  </div>
                </div>
              </div>
            </AnimatedSection>

            {/* 4. Report Generation */}
            <AnimatedSection>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
                <div className="lg:col-span-3 order-2 lg:order-1">
                  <div className="bg-white rounded-2xl shadow-xl border border-medical-200/50 overflow-hidden">
                    <div className="p-5">
                      <p className="text-sm font-medium text-medical-900 mb-4 flex items-center gap-1.5">
                        <Zap className="h-4 w-4 text-amber-500" /> Draft a new report
                      </p>
                      <div className="space-y-3">
                        {/* Key Findings */}
                        <div className="p-4 rounded-xl border-2 border-amber-200 bg-amber-50/30 flex items-center justify-between group cursor-pointer hover:border-amber-300 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                              <Star className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-amber-900">Key Findings Report</p>
                              <p className="text-xs text-amber-600">Synthesise the starred findings into a structured report</p>
                            </div>
                          </div>
                          <ArrowRight className="h-4 w-4 text-amber-400" />
                        </div>
                        {/* Chronology Summary */}
                        <div className="p-4 rounded-xl border border-primary-200 bg-primary-50/30 flex items-center justify-between group cursor-pointer hover:border-primary-300 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                              <Clock className="h-5 w-5 text-primary-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-medical-900">Chronology Summary</p>
                              <p className="text-xs text-medical-500">Structured chronological narrative of the medical records</p>
                            </div>
                          </div>
                          <ArrowRight className="h-4 w-4 text-medical-300" />
                        </div>
                        {/* Mirror report */}
                        <div className="p-4 rounded-xl border border-dashed border-medical-200 flex items-center justify-between group cursor-pointer hover:border-medical-300 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-medical-100 rounded-lg flex items-center justify-center">
                              <FileText className="h-5 w-5 text-medical-500" />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-medical-900">Mirror a previous report</p>
                              <p className="text-xs text-medical-500">Upload a Word report to create a reusable template</p>
                            </div>
                          </div>
                          <ArrowRight className="h-4 w-4 text-medical-300" />
                        </div>
                        {/* Templates */}
                        <div className="p-4 rounded-xl border border-dashed border-medical-200 flex items-center justify-between group cursor-pointer hover:border-medical-300 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-medical-100 rounded-lg flex items-center justify-center">
                              <FileText className="h-5 w-5 text-medical-400" />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-medical-900">Use a template...</p>
                              <p className="text-xs text-medical-500">Browse specialism-specific report templates</p>
                            </div>
                          </div>
                          <ArrowRight className="h-4 w-4 text-medical-300" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2 order-1 lg:order-2 lg:sticky lg:top-32">
                  <div className="inline-flex items-center px-3 py-1 bg-amber-50 rounded-full text-xs font-medium text-amber-600 mb-3">Reports</div>
                  <h3 className="text-2xl font-bold text-medical-900 mb-3 font-display">Generate reports from your case data</h3>
                  <p className="text-medical-600 text-sm leading-relaxed mb-4">
                    Produce structured reports directly from the analysed case. Key findings, chronology summaries, or full expert reports drafted from the evidence with inline citations. Upload your own report as a template, or use ours.
                  </p>
                  <ul className="space-y-2 text-sm text-medical-600">
                    <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>AI-drafted reports grounded in the case evidence</span></li>
                    <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>Mirror your own report format as a reusable template</span></li>
                    <li className="flex items-start space-x-2"><CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" /><span>Edit inline and export to Word</span></li>
                  </ul>
                </div>
              </div>
            </AnimatedSection>

          </div>
        </div>
      </section>

      {/* ROI / Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white/50 to-primary-50/20">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold text-medical-900 mb-14 font-display text-center">
              Reduce Preparation Time by Up to 70%
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Zap,
                title: 'Faster Case Preparation',
                description: 'Produce chronologies in minutes, not weeks.',
                iconColor: 'text-cyan-600',
                iconBg: 'from-cyan-50 to-cyan-100',
              },
              {
                icon: Eye,
                title: 'Earlier Liability Insight',
                description: 'Identify key issues before expert instruction.',
                iconColor: 'text-purple-600',
                iconBg: 'from-purple-50 to-purple-100',
              },
              {
                icon: TrendingDown,
                title: 'Lower Internal Cost',
                description: 'Reduce fee earner time spent on manual review.',
                iconColor: 'text-teal-600',
                iconBg: 'from-teal-50 to-teal-100',
              },
              {
                icon: CheckCircle,
                title: 'Improved Consistency',
                description: 'Standardise record review across teams.',
                iconColor: 'text-green-600',
                iconBg: 'from-green-50 to-green-100',
              },
            ].map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-medical-200/50 text-center h-full">
                  <div className={`w-12 h-12 bg-gradient-to-br ${item.iconBg} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <item.icon className={`h-6 w-6 ${item.iconColor}`} />
                  </div>
                  <h3 className="font-semibold text-medical-900 mb-2 text-sm sm:text-base">{item.title}</h3>
                  <p className="text-medical-600 text-xs sm:text-sm leading-relaxed">{item.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>


      {/* Trust / Compliance Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white/50 to-primary-50/20">
        <div className="max-w-3xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold text-medical-900 mb-10 font-display">
              Built for Defensible Litigation Workflows
            </h2>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <div className="space-y-5 max-w-lg mx-auto text-left">
              {[
                'Human-in-the-loop review',
                'Full audit trail of extracted evidence',
                'GDPR-compliant processing with UK data residency',
                'Designed to support—not replace—professional judgment',
              ].map((point, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-medical-700 leading-relaxed">{point}</p>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <Link
                to="/security"
                onClick={() => window.scrollTo(0, 0)}
                className="text-primary-600 hover:text-primary-700 font-medium text-sm inline-flex items-center transition-colors"
              >
                View full security overview
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white/50 to-medical-50/50">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="relative bg-gradient-to-br from-primary-600 to-primary-700 rounded-3xl p-10 sm:p-14 text-center overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-3xl" />

              <div className="relative">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-display">
                  See How ClinLexis Can Accelerate Your Next Case
                </h2>
                <p className="text-primary-100 text-lg mb-8 max-w-xl mx-auto">
                  Book a personalised demonstration and see how ClinLexis fits into your litigation workflow.
                </p>
                <Link
                  to="/demo"
                  onClick={() => window.scrollTo(0, 0)}
                  className="inline-flex items-center px-8 py-4 bg-white text-primary-700 rounded-xl font-semibold hover:bg-primary-50 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Watch the Demo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-medical-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <Logo size="md" className="mb-4" variant="dark" />
              <p className="text-medical-300 mb-6 max-w-md leading-relaxed">
                AI-powered medical evidence platform for secure, compliant medico-legal case management —
                informed by practising UK clinicians.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-medical-800 border border-medical-700 rounded-full text-xs text-medical-300">
                  <ShieldCheck className="h-3 w-3" />
                  <span>NHS DSPT</span>
                </span>
                <span className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-medical-800 border border-medical-700 rounded-full text-xs text-medical-300">
                  <MapPin className="h-3 w-3" />
                  <span>UK Data Residency</span>
                </span>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-white">Product</h3>
              <ul className="space-y-3 text-medical-300">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><Link to="/security" onClick={() => window.scrollTo(0, 0)} className="hover:text-white transition-colors">Security</Link></li>
                <li><Link to="/signup" onClick={() => window.scrollTo(0, 0)} className="hover:text-white transition-colors">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-white">Company</h3>
              <ul className="space-y-3 text-medical-300">
                <li><Link to="/team" onClick={() => window.scrollTo(0, 0)} className="hover:text-white transition-colors">Team</Link></li>
                <li><Link to="/jobs" onClick={() => window.scrollTo(0, 0)} className="hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/demo" onClick={() => window.scrollTo(0, 0)} className="hover:text-white transition-colors">Watch the Demo</Link></li>
                <li><Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-medical-800 pt-8 space-y-2">
            <p className="text-sm text-medical-400 text-center">
              &copy; {new Date().getFullYear()} ClinLexis™. All rights reserved.
            </p>
            <p className="text-xs text-medical-500 text-center">
              ClinLexis Ltd &middot; Company No. 17081483 &middot; Registered in England and Wales &middot; 238a Telegraph Road, Heswall, Wirral, CH60 0AL
            </p>
            <p className="text-xs text-medical-500 text-center">
              <Link to="/terms" onClick={() => window.scrollTo(0, 0)} className="hover:text-white transition-colors">Terms of Service</Link>
              {' '}&middot;{' '}
              <Link to="/privacy" onClick={() => window.scrollTo(0, 0)} className="hover:text-white transition-colors">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
